// Tested in ../index.test.tsx

import SessionProgressBar from '@/components/session-progress-bar';
import { useProgramActions } from '@/hooks/use-program-actions';
import { MediaGuideMetadata } from '@/utils/media/types';
import { EnumFeature } from '@calm/evexia-analytics';
import { DS_HEIGHT } from '@calm/evexia-design-tokens';
import {
    BANNER_HEIGHT,
    BANNER_WIDTH,
} from '@rhg/components/ui/hero-banner-image/types';
import { MaxColumnsContainer } from '@rhg/components/ui/layout';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { useProgramWorkflowActions } from '@rhg/hooks/use-program-workflow-actions';
import { useProvidedProgramContextWithGuidesAndWorkflow } from '@rhg/hooks/use-provided-program-context/with-guides-and-workflow';
import { useUserBlocker } from '@rhg/hooks/use-user-blocker';
import { UserBlocker } from '@rhg/hooks/use-user-blocker/types';
import commonMessages from '@rhg/lang/messages/common';
import programMessages from '@rhg/lang/messages/program';
import { logger } from '@rhg/libs/logger';
import {
    Box,
    Button,
    ButtonProps,
    Divider,
    Grid,
    Tab,
    Tabs,
    Theme,
} from '@rhg/libs/material';
import { getProgramId, ProgramState } from '@rhg/utils/programs';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { AccessCodeBanner } from '../../access-code-banner';
import { GetStartedDialog } from '../get-started-dialog';
import { ProgramOverview } from '../overview';
import { ProgramHeader } from '../program-header';
import { ProgramSchedule } from '../schedule';
import { ProgramTab } from './types';

const styleContainer = {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    minHeight: `calc(100dvh - ${DS_HEIGHT.TOOLBAR}px)`,
};
const styleContentContainer = {
    flex: 1,
    width: '100%',
    background: (theme: Theme) => theme.palette.gradient.backgroundPrimary,
    display: 'flex',
    justifyContent: 'center',
    pt: (theme: Theme) => theme.ds.spacingUnits.CONTENT,
    px: (theme: Theme) => ({
        xs: theme.ds.spacingUnits.SIDE_SMALL,
        md: theme.ds.spacingUnits.SIDE_MEDIUM,
        lg: theme.ds.spacingUnits.SIDE_LARGE,
    }),
};
const styleContent = {
    width: '100%',
    mb: (theme: Theme) => theme.ds.spacingUnits.SECTION,
};
const styleBannerContainer = {
    width: BANNER_WIDTH,
    height: { xs: 'auto', sm: 'auto', md: BANNER_HEIGHT },
    backgroundColor: (theme: Theme) => theme.palette.background.primary,
};

const styleDivider = {
    width: '100%',
    mb: (theme: Theme) => theme.ds.spacingUnits.CONTENT,
};
const styleAccessCodeBanner = {
    mb: (theme: Theme) => theme.ds.spacingUnits.CONTENT,
};
const styleActions = {
    position: 'sticky',
    bottom: 0,
    width: '100%',
    justifyContent: 'center',
    py: 2,
    px: (theme: Theme) => ({
        xs: theme.ds.spacingUnits.SIDE_SMALL,
        md: theme.ds.spacingUnits.SIDE_MEDIUM,
        lg: theme.ds.spacingUnits.SIDE_LARGE,
    }),
    alignItems: 'center',
    display: 'flex',
    zIndex: 2,
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? 'background.paper'
            : 'background.primary',
    borderTop: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? `1px solid ${theme.palette.divider}`
            : 'none',
};
const styleAction = { width: '100%' };
const styleProgress = {
    mb: (theme: Theme) => theme.ds.spacingUnits.CONTENT,
};

export interface ContextAwareProgramProps {
    showOverview?: boolean;
}

export const ContextAwareProgram = ({
    showOverview,
}: ContextAwareProgramProps) => {
    const {
        numSessionsCompleted,
        programType,
        programState,
        numSessions,
        program,
        workflow,
        currentWorkflowStep,
        packClass,
        firstSession,
    } = useProvidedProgramContextWithGuidesAndWorkflow();

    const { formatMessage } = useIntl();
    const { userBlocker } = useUserBlocker();
    const {
        menuAnchor,
        getStartedDialogState,
        showHowToGetStartedDialog,
        openMenu,
        closeMenu,
        handleGetStartedClosed,
        onLeaveProgram,
        onJoinProgram,
        onRestartProgram,
    } = useProgramWorkflowActions({
        program,
        workflow,
        currentWorkflowStep,
        packClass,
    });
    const { playGuide } = useProgramActions();

    const scrollingContainerRef = useRef<HTMLElement>(null);
    const canShowOverview = programType === 'clinical_program';
    const [selectedTab, setSelectedTab] = useState<ProgramTab>(
        canShowOverview &&
            (![ProgramState.InProgress, ProgramState.Completed].includes(
                programState
            ) ||
                showOverview)
            ? 'overview'
            : 'schedule'
    );

    const isUnsponsored = useMemo(
        () => userBlocker === UserBlocker.Unsponsored,
        [userBlocker]
    );

    // TODO(EVE-10506): callers currently log failures to Datadog only; needs user-facing error UX
    const handleJoinProgram = useCallback(
        async ({
            willResetProgress,
            clickedGuide,
        }: {
            willResetProgress: boolean;
            clickedGuide?: MediaGuideMetadata;
        }) => {
            await onJoinProgram({
                onSuccess: () => {
                    setSelectedTab('schedule');
                    const guide = clickedGuide ?? firstSession;
                    if (guide) playGuide(guide);
                },
                willResetProgress,
                selectedGuide: clickedGuide ?? firstSession,
            });
        },
        [firstSession, onJoinProgram, playGuide]
    );

    // TODO(EVE-10506): callers currently log failures to Datadog only; needs user-facing error UX
    const handleLeaveProgram = useCallback(async () => {
        await onLeaveProgram({ onSuccess: () => setSelectedTab('overview') });
    }, [onLeaveProgram]);

    const handleRestartProgram = useCallback(() => {
        onRestartProgram({
            onSuccess: () => {
                setSelectedTab('schedule');
                if (firstSession) playGuide(firstSession);
            },
        }).catch((e) =>
            logger.error('Error restarting program', {}, e as Error)
        );
    }, [firstSession, onRestartProgram, playGuide]);

    const isSmOrXs = useIsMobile();

    const ctaButtonProps: Pick<ButtonProps, 'trackButton' | 'onClick'> =
        useMemo(() => {
            if (programState === ProgramState.InProgress) {
                return {} as ButtonProps;
            }
            if (programState === ProgramState.Completed) {
                return {
                    trackButton: 'CtaRestartProgram',
                    onClick: handleRestartProgram,
                };
            }
            return {
                trackButton: 'CtaJoinProgram',
                ...(program
                    ? {
                          trackProps: {
                              ProgramName: program.title,
                              ProgramId: getProgramId(program),
                          },
                      }
                    : {}),
                trackFeature: EnumFeature.ClinicalProgram,
                onClick: () => {
                    handleJoinProgram({ willResetProgress: false }).catch(
                        (err) =>
                            logger.error(
                                'Failed to join program',
                                {},
                                err as Error
                            )
                    );
                },
            };
        }, [handleJoinProgram, handleRestartProgram, program, programState]);

    return (
        <Box ref={scrollingContainerRef} sx={styleContainer}>
            <Box sx={styleBannerContainer}>
                <ProgramHeader
                    onJoinProgramClicked={() => {
                        handleJoinProgram({ willResetProgress: false }).catch(
                            (err) =>
                                logger.error(
                                    'Failed to join program',
                                    {},
                                    err as Error
                                )
                        );
                    }}
                    onLeaveProgramClicked={() => {
                        handleLeaveProgram().catch((err) =>
                            logger.error(
                                'Failed to leave program',
                                {},
                                err as Error
                            )
                        );
                    }}
                    onShowHowToGetStartedDialogClicked={() =>
                        showHowToGetStartedDialog()
                    }
                    closeMoreMenu={closeMenu}
                    moreAnchor={menuAnchor}
                    openMoreMenu={openMenu}
                    onRestartProgramClicked={handleRestartProgram}
                />
            </Box>
            <Box sx={styleContentContainer}>
                <MaxColumnsContainer columns={6}>
                    <Box sx={styleContent}>
                        {canShowOverview && (
                            <>
                                <Tabs
                                    value={selectedTab}
                                    onChange={(_, newTab: ProgramTab) =>
                                        setSelectedTab(newTab)
                                    }
                                    textColor="secondary"
                                    indicatorColor="secondary"
                                    centered
                                >
                                    <Tab
                                        trackButton="ProgramScheduleTab"
                                        value="schedule"
                                        label={formatMessage(
                                            commonMessages['common.schedule']
                                        )}
                                        id="schedule-tab"
                                        aria-controls="schedule-tabpanel"
                                    />
                                    <Tab
                                        trackButton="ProgramOverviewTab"
                                        value="overview"
                                        label={formatMessage(
                                            commonMessages['common.overview']
                                        )}
                                        id="overview-tab"
                                        aria-controls="overview-tabpanel"
                                    />
                                </Tabs>
                                <Divider
                                    orientation="horizontal"
                                    sx={styleDivider}
                                    aria-hidden
                                />
                            </>
                        )}
                        {isUnsponsored && (
                            <Grid item xs={12}>
                                <AccessCodeBanner sx={styleAccessCodeBanner} />
                                <Divider
                                    aria-hidden
                                    orientation="horizontal"
                                    sx={styleDivider}
                                />
                            </Grid>
                        )}
                        {isSmOrXs &&
                            programType === 'clinical_program' &&
                            programState === ProgramState.InProgress &&
                            numSessions && (
                                <SessionProgressBar
                                    currentStep={numSessionsCompleted}
                                    totalSteps={numSessions}
                                    sx={styleProgress}
                                    isClinicalProgram={
                                        programType === 'clinical_program'
                                    }
                                />
                            )}
                        {canShowOverview && (
                            <Box
                                role="tabpanel"
                                id="overview-tabpanel"
                                aria-labelledby="overview-tab"
                                tabIndex={0}
                                hidden={selectedTab !== 'overview'}
                            >
                                {selectedTab === 'overview' && (
                                    <ProgramOverview />
                                )}
                            </Box>
                        )}
                        {(canShowOverview || selectedTab === 'schedule') && (
                            <Box
                                {...(canShowOverview
                                    ? {
                                          role: 'tabpanel',
                                          id: 'schedule-tabpanel',
                                          'aria-labelledby': 'schedule-tab',
                                          tabIndex: 0,
                                          hidden: selectedTab !== 'schedule',
                                      }
                                    : {})}
                            >
                                {selectedTab === 'schedule' && (
                                    <ProgramSchedule
                                        onJoin={(guide: MediaGuideMetadata) => {
                                            handleJoinProgram({
                                                willResetProgress: false,
                                                clickedGuide: guide,
                                            }).catch((err) =>
                                                logger.error(
                                                    'Failed to join program',
                                                    {},
                                                    err as Error
                                                )
                                            );
                                        }}
                                    />
                                )}
                            </Box>
                        )}
                    </Box>
                </MaxColumnsContainer>
            </Box>
            {programType === 'clinical_program' &&
                programState !== ProgramState.InProgress && (
                    <Box sx={styleActions}>
                        <MaxColumnsContainer columns={6}>
                            <Button
                                size="large"
                                variant="contained"
                                color="primary"
                                {...ctaButtonProps}
                                sx={styleAction}
                            >
                                {programState === ProgramState.Completed
                                    ? formatMessage(
                                          programMessages[
                                              'program.restart_program'
                                          ]
                                      )
                                    : formatMessage(
                                          programMessages[
                                              'program.join_program'
                                          ]
                                      )}
                            </Button>
                        </MaxColumnsContainer>
                    </Box>
                )}
            <GetStartedDialog
                {...getStartedDialogState}
                onClose={handleGetStartedClosed}
            />
        </Box>
    );
};
